import { finalize } from "rxjs";
import { EvaService } from "src/app/core";
import { MessageService } from "primeng/api";
import { EvaCompany, EvaFeatures } from "src/app/domains/internal";
import { BexioService } from "src/app/features/accounting/services/bexio.service";
import { Employee } from "src/app/features/employees/domains/employee";
import { EmployeesService } from "src/app/features/employees/services/employees.service";
import { EvaCompanyService } from "src/app/features/eva-company/services/evaCompany.service";
import { EvaSubscriptionService } from "src/app/features/subscription/eva-subscription.service";
import { EvaSubscription } from "src/app/features/subscription/models/eva-subscription.model";
import { AuthenticationService } from "src/app/services/authentication.service";
import { environment } from "src/environments/environment";

import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { provideIcons } from "@ng-icons/core";
import {
  heroArrowPath,
  heroAdjustmentsHorizontal,
  heroArrowRightStartOnRectangle,
  heroArrowsRightLeft,
  heroArrowLeftCircle,
  heroArrowRightCircle,
  heroBell,
  heroChatBubbleLeftRight,
  heroBookOpen,
  heroCalculator,
  heroCalendar,
  heroChartBar,
  heroCheck,
  heroChevronDown,
  heroChevronUp,
  heroChevronUpDown,
  heroClipboardDocumentCheck,
  heroClock,
  heroCog6Tooth,
  heroCreditCard,
  heroDocumentArrowDown,
  heroDocumentArrowUp,
  heroDocumentDuplicate,
  heroDocumentText,
  heroHome,
  heroLockClosed,
  heroMagnifyingGlass,
  heroPaperAirplane,
  heroPower,
  heroQuestionMarkCircle,
  heroRocketLaunch,
  heroUser,
  heroUserCircle,
  heroUserGroup,
  heroUsers,
  heroWrench,
  heroWrenchScrewdriver,
} from "@ng-icons/heroicons/outline";

import { EvaLayoutService } from "../eva-layout.service";

import { ChiftService } from "src/app/features/accounting/services/chift.service";
import { TopalService } from "src/app/features/accounting/services/topal.service";
import { CommandPaletteService } from "src/app/services/command-palette.service";
import { EvaHelpService } from "src/app/shared/ds/eva-help-drawer/eva-help.service";

@Component({
  selector: "app-eva-sidebar",
  templateUrl: "./eva-sidebar.component.html",
  styleUrls: ["./eva-sidebar.component.scss"],
  viewProviders: [
    provideIcons({
      heroArrowPath,
      heroUsers,
      heroUserCircle,
      heroUserGroup,
      heroCalculator,
      heroHome,
      heroCalendar,
      heroWrenchScrewdriver,
      heroWrench,
      heroDocumentDuplicate,
      heroDocumentArrowUp,
      heroBookOpen,
      heroDocumentText,
      heroDocumentArrowDown,
      heroPaperAirplane,
      heroCreditCard,
      heroChartBar,
      heroClipboardDocumentCheck,
      heroArrowsRightLeft,
      heroAdjustmentsHorizontal,
      heroQuestionMarkCircle,
      heroLockClosed,
      heroMagnifyingGlass,
      heroRocketLaunch,
      // New icons for sidebar chrome
      heroChevronDown,
      heroChevronUp,
      heroChevronUpDown,
      heroCheck,
      heroUser,
      heroClock,
      heroPower,
      heroCog6Tooth,
      heroBell,
      heroArrowRightStartOnRectangle,
      heroArrowLeftCircle,
      heroArrowRightCircle,
      heroChatBubbleLeftRight,
    }),
  ],
})
export class EvaSidebarComponent implements OnInit {
  @Input() isMobile: boolean = false;
  @Input() isMenuCollapsed: boolean = false;
  @Input() isMenuHovered: boolean = false;
  @Input() isMobileMenuToggled: boolean = false;
  @Output() sidebarVisibleChange = new EventEmitter<boolean>();
  @ViewChild("companySearchInput") companySearchInput: ElementRef<HTMLInputElement>;

  currentEmployee: Employee = this.evaCompanyService.getCurrentEvaEmployee();
  currentEvaCompany: EvaCompany = this.evaCompanyService.getCurrentEvaCompany();

  appVersion = environment.appVersion;
  showAppVersion = false;
  newVersionAvailable = false;

  showAdminIcon = false;

  menuItems = [];

  showUpgradeFormula = false;
  upgradeFormulaPopup = false;

  private destroyRef = inject(DestroyRef);

  evaSubscription: EvaSubscription;

  // Company selector
  currentEvaCompanyId: string = "";
  currentEvaCompaniesAvailable: EvaCompany[] = [];
  evaCompaniesDropdownOptions: any[] = [];
  dataLoaded: boolean = false;
  companiesLoading: boolean = false;
  companyDropdownVisible: boolean = false;
  companySearchQuery: string = "";
  workspaceMenuVisible: boolean = false;
  overlayFocusIndex: number = -1;

  showFeedbackModal = false;

  openFeedbackModal(): void {
    this.showFeedbackModal = true;
  }

  /** Filtered companies based on search query */
  get filteredCompanies(): EvaCompany[] {
    if (!this.companySearchQuery.trim()) {
      return this.currentEvaCompaniesAvailable;
    }
    const query = this.normalizeSearch(this.companySearchQuery);
    return this.currentEvaCompaniesAvailable.filter((c) => this.normalizeSearch(c.name).includes(query));
  }

  private normalizeSearch(value: string | undefined | null): string {
    return (value ?? "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
  }

  // User profile
  userImagePath: string = "";
  userMenuVisible: boolean = false;
  hasAccessToPlanning: boolean = false;

  // Notifications
  notificationCount: number = 0;

  private readonly planningAccessCompanyIds: string[] = [
    "5e9e2215356088ad932381a4",
    "5e9e2215356088ad932381a5",
    "5e9e2215356088ad932381a8",
    "658012d4517a544a7fcd9aa6",
    "658012be517a544a7fcd9a7d",
  ];

  bexioStatus: "NOT_ACTIVATED" | "DEACTIVATED" | "ACTIVATED" = "NOT_ACTIVATED";

  topalConnection: "ACTIVATED" | "DEACTIVATED" = "DEACTIVATED";

  chiftConnections = [];

  get hasAccountingConnections() {
    return this.bexioStatus === "ACTIVATED" || this.chiftConnections.length > 0 || this.topalConnection === "ACTIVATED";
  }
  private static readonly HIDE_COMMERCIAL_ITEMS_KEY = "hide_commercial_items";
  private static readonly HIDE_COMMERCIAL_ITEMS_TIMESTAMP_KEY = "hide_commercial_items_timestamp";
  private static readonly HIDE_COMMERCIAL_ITEMS_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

  showCommercialItems: boolean = true;

  /** Whether multiple companies are available */
  get hasMultipleCompanies(): boolean {
    return this.currentEvaCompaniesAvailable?.length > 1;
  }

  /** Whether the sidebar is in minimized mode (collapsed, not hovered, desktop) */
  get isMinimized(): boolean {
    return this.isMenuCollapsed && !this.isMenuHovered && !this.isMobile;
  }

  constructor(
    private evaCompanyService: EvaCompanyService,
    private router: Router,
    private messageService: MessageService,
    private evaLayoutService: EvaLayoutService,
    private evaSubscriptionService: EvaSubscriptionService,
    private bexioService: BexioService,
    private chiftService: ChiftService,
    private topalService: TopalService,
    private authService: AuthenticationService,
    private employeesService: EmployeesService,
    private commandPaletteService: CommandPaletteService,
    private evaHelpService: EvaHelpService,
    private evaService: EvaService,
  ) {}

  openCommandPalette(): void {
    this.commandPaletteService.open();
  }

  openHelp(): void {
    this.evaHelpService.open("sidebar");
    this.closeSidebar();
  }

  ngOnInit() {
    this.initializeCommercialItemsVisibility();
    this.getBexioTokenStatus();
    this.getChiftConnections();
    this.getTopalConnection();

    // Company selector
    this.currentEvaCompanyId = this.evaCompanyService.currentEvaCompany?.eva_company_id;
    this.fetchEvaCompanies();

    this.evaCompanyService.companyUpdated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((updated) => {
      const idx = this.currentEvaCompaniesAvailable.findIndex((c) => c.eva_company_id === updated.eva_company_id);
      if (idx === -1) return;
      const next = [...this.currentEvaCompaniesAvailable];
      next[idx] = updated;
      this.currentEvaCompaniesAvailable = next;
      this.refreshEvaCompaniesDropdownOptions();
    });

    // User avatar
    this.loadAvatar();

    this.evaSubscriptionService.currentSubscription$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((subscription) => {
        this.evaSubscription = subscription;
        this.showUpgradeFormula =
          this.evaSubscription?.formula_type !== "EXPERT" && this.currentEmployee.employee_type === "ADMIN";

        this.hasAccessToPlanning = !!(
          subscription?.hasAccessToPlanning?.() && this.planningAccessCompanyIds.includes(subscription.eva_company_id)
        );

        this.updateMenuItems();
      });

    this.showAdminIcon = this.currentEmployee.employee_type === "ADMIN";
    this.buildAdminMenuItems();

    this.updateMenuItems();
  }
  showEvaCopyright = false;
  @HostListener("window:keydown", ["$event"])
  handleKeyDown(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.key === "e") {
      event.preventDefault();
      this.showEvaCopyright = !this.showEvaCopyright;
    }
  }

  getBexioTokenStatus() {
    this.bexioService
      .getBexioTokenStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.bexioStatus = res["token_status"];
      });
  }

  getChiftConnections() {
    this.chiftService
      .getChiftConnections()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.chiftConnections = res["connections"];
      });
  }

  getTopalConnection() {
    this.topalService
      .getTopalConnection()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.topalConnection = res["status"];
      });
  }

  closeSidebar(): void {
    this.evaLayoutService.toggleMobileMenuToggled();
  }

  expandSidebar(): void {
    if (!this.isMobile) {
      this.evaLayoutService.toggleMenuHovered(true);
    }
  }

  collapseSidebar(): void {
    setTimeout(() => {
      this.evaLayoutService.toggleMenuHovered(false);
    }, 300);
  }

  clickVersion() {
    this.showAppVersion = !this.showAppVersion;

    this.evaCompanyService.refreshEvaCompanies();
  }

  promptUserToUpdate(): void {
    this.messageService.clear("c");
    this.messageService.add({ key: "c", sticky: true, severity: "info" });
  }

  onConfirmUpdate() {
    this.messageService.clear("c");
  }

  onRejectUpdate() {
    this.messageService.clear("c");
  }

  private initializeCommercialItemsVisibility() {
    const hidePreference = localStorage.getItem(EvaSidebarComponent.HIDE_COMMERCIAL_ITEMS_KEY);
    const storedTimestamp = localStorage.getItem(EvaSidebarComponent.HIDE_COMMERCIAL_ITEMS_TIMESTAMP_KEY);

    if (hidePreference !== "true") {
      this.showCommercialItems = true;
      return;
    }

    if (!storedTimestamp) {
      this.resetCommercialItemsPreference();
      return;
    }

    const hideTimestamp = parseInt(storedTimestamp, 10);
    const isExpired =
      Number.isNaN(hideTimestamp) || Date.now() - hideTimestamp > EvaSidebarComponent.HIDE_COMMERCIAL_ITEMS_TTL;

    if (isExpired) {
      this.resetCommercialItemsPreference();
      return;
    }

    this.showCommercialItems = false;
  }

  private resetCommercialItemsPreference() {
    localStorage.removeItem(EvaSidebarComponent.HIDE_COMMERCIAL_ITEMS_KEY);
    localStorage.removeItem(EvaSidebarComponent.HIDE_COMMERCIAL_ITEMS_TIMESTAMP_KEY);
    this.showCommercialItems = true;
  }

  private updateMenuItems() {
    if (this.evaSubscription?.formula_type === "EXPERT") {
      this.updateMenuItemsExpert();
    } else {
      this.updateMenuItemsLight();
    }
  }

  private updateMenuItemsLight() {
    if (!this.currentEmployee) {
      return;
    }

    let tmpMenuItems = [];

    // META ITEMS

    let metaItems = [];

    if (this.evaSubscription?.status === "TRIAL" || this.evaSubscription?.isOnboardingActive()) {
      metaItems.push({ path: "/getting-started", title: "Bien demarrer", icon: "heroRocketLaunch", class: "" });
    }

    metaItems.push({ path: "/home", title: "Tableau de bord", icon: "heroHome", class: "", category: "" });
    if (this.currentEmployee.employee_type !== "ACCOUNTANT") {
      if (this.showCommercialItems || this.evaSubscription?.hasAccessToProjects()) {
        metaItems.push({
          path: "/projects",
          title: "Chantiers",
          icon: "heroWrenchScrewdriver",
          class: "",
          category: this.evaSubscription?.hasAccessToProjects() ? "" : "pro",
        });
      }
      if (this.showCommercialItems) {
        metaItems.push({
          path: "/admin/team-planning",
          title: "Planning",
          icon: "heroCalendar",
          class: "",
          category: this.evaSubscription?.hasAccessToPlanning() ? "" : "pro",
        });
        metaItems.push({
          path: "/interventions",
          title: "Interventions",
          icon: "heroWrench",
          class: "",
          category: this.currentEvaCompany.hasFeature(EvaFeatures.MAINTENANCE_CONTRACT) ? "" : "pro",
        });
      }
    }

    tmpMenuItems.push({ label: "", items: metaItems });

    // SALES ITEMS

    let salesItems = [];

    if (this.currentEmployee.employee_type !== "ACCOUNTANT") {
      salesItems = [
        { path: "/offers", title: "Offres", icon: "heroDocumentDuplicate", class: "", category: "" },
        { path: "/invoices/issued", title: "Factures clients", icon: "heroDocumentArrowUp", class: "", category: "" },
        { path: "/customers", title: "Clients & Prospects", icon: "heroUsers", class: "", category: "" },
        { path: "/catalog", title: "Catalogues", icon: "heroBookOpen", class: "", category: "" },
      ];
    } else {
      salesItems = [
        { path: "/invoices/issued", title: "Factures clients", icon: "heroDocumentArrowUp", class: "", category: "" },
      ];
    }

    tmpMenuItems.push({ label: "VENTES", items: salesItems });

    // PURCHASE ITEMS
    let purchaseItems = [];

    if (this.currentEmployee.employee_type !== "ACCOUNTANT") {
      if (this.showCommercialItems || this.evaSubscription?.hasAccessToPurchases()) {
        purchaseItems = [
          {
            path: "/purchase-orders",
            title: "Bons de commande",
            icon: "heroDocumentText",
            class: "",
            category: this.evaSubscription?.hasAccessToPurchases() ? "" : "pro",
          },
          {
            path: "/invoices/received",
            title: "Factures d'achat",
            icon: "heroDocumentArrowDown",
            class: "",
            category: this.evaSubscription?.hasAccessToPurchases() ? "" : "pro",
          },
          {
            path: "/suppliers",
            title: "Fournisseurs",
            icon: "heroUserGroup",
            class: "",
            category: this.evaSubscription?.hasAccessToPurchases() ? "" : "pro",
          },
        ];
      }
      if (this.showCommercialItems || this.currentEvaCompany.hasFeature(EvaFeatures.BANKING)) {
        purchaseItems.push({
          path: "/admin/banking",
          title: "Banking",
          icon: "heroArrowsRightLeft",
          class: "",
          category: this.currentEvaCompany.hasFeature(EvaFeatures.BANKING) ? "" : "pro",
        });
      }
    } else {
      purchaseItems = [
        {
          path: "/invoices/received",
          title: "Factures d'achat",
          icon: "heroDocumentArrowDown",
          class: "",
          category: this.evaSubscription?.hasAccessToPurchases() ? "" : "pro",
        },
      ];
    }

    if (purchaseItems.length > 0) {
      tmpMenuItems.push({ label: "DÉPENSES", items: purchaseItems });
    }

    // FOOTER ITEMS
    let footerItems = [];

    if (this.currentEvaCompany.hasFeature(EvaFeatures.ACCOUNTING) && this.evaSubscription?.hasAccessToAccounting()) {
      footerItems.push({ path: "/accounting", title: "Comptabilite", icon: "heroCalculator", class: "" });
    }

    if (footerItems.length > 0) {
      tmpMenuItems.push({ label: "", items: footerItems, topSeparator: true });
    }

    this.menuItems = tmpMenuItems.slice();
  }

  private updateMenuItemsExpert() {
    if (!this.currentEmployee) {
      return;
    }

    this.menuItems = [];

    if (this.currentEmployee.employee_type === "ADMIN") {
      let metaItems = [];

      if (this.evaSubscription?.status === "TRIAL" || this.evaSubscription?.isOnboardingActive()) {
        metaItems.push({ path: "/getting-started", title: "Bien demarrer", icon: "heroRocketLaunch", class: "" });
      }
      metaItems.push({ path: "/home", title: "Tableau de bord", icon: "heroHome", class: "" });
      metaItems.push({ path: "/projects", title: "Chantiers", icon: "heroWrenchScrewdriver", class: "" });
      metaItems.push({
        path: "/admin/team-planning",
        title: "Planning",
        icon: "heroCalendar",
        class: "",
        category: "",
      });

      const salesItems = [
        { path: "/offers", title: "Offres", icon: "heroDocumentDuplicate", class: "" },
        { path: "/invoices/issued", title: "Factures clients", icon: "heroDocumentArrowUp", class: "" },
        { path: "/customers", title: "Clients & Prospects", icon: "heroUsers", class: "" },
      ];

      const purchaseItems = [
        { path: "/purchase-orders", title: "Bons de commande", icon: "heroDocumentText", class: "" },
        { path: "/invoices/received", title: "Factures d'achat", icon: "heroDocumentArrowDown", class: "" },
        { path: "/suppliers", title: "Fournisseurs", icon: "heroUserGroup", class: "" },
      ];

      const financeItems = [];
      if (this.currentEmployee.employee_position_current.admin_allow_expense_reports) {
        financeItems.push({ path: "/expenses-report", title: "Notes de frais", icon: "heroCreditCard", class: "" });
      }

      if (this.currentEvaCompany.hasFeature(EvaFeatures.MAINTENANCE_CONTRACT)) {
        metaItems.push({ path: "/interventions", title: "Interventions", icon: "heroWrench", class: "" });
      }
      if (this.currentEvaCompany.hasFeature(EvaFeatures.SCAN_EMAIL)) {
        purchaseItems.push({ path: "/scan-emails", title: "Scan Emails", icon: "heroPaperAirplane", class: "" });
      }
      if (this.currentEvaCompany.hasFeature(EvaFeatures.CATALOG)) {
        salesItems.push({ path: "/catalog", title: "Catalogues", icon: "heroBookOpen", class: "" });
      }
      if (
        this.currentEvaCompany.hasFeature(EvaFeatures.BANKING) &&
        this.currentEmployee.employee_position_current.admin_allow_banking
      ) {
        financeItems.push({ path: "/admin/banking", title: "Banking", icon: "heroArrowsRightLeft", class: "" });
      }
      if (
        this.currentEvaCompany.hasFeature(EvaFeatures.ACCOUNTING) &&
        this.currentEmployee.employee_position_current.admin_allow_accounting
      ) {
        financeItems.push({ path: "/accounting", title: "Comptabilité", icon: "heroCalculator", class: "" });
      }

      let footerItems = [
        { path: "/employees", title: "Collaborateurs", icon: "heroUserCircle", class: "", category: "" },
        { path: "/contacts", title: "Contacts", icon: "heroBookOpen", class: "", category: "" },
      ];

      this.menuItems.push(
        { label: "", items: metaItems },
        { label: "VENTES", items: salesItems },
        { label: "DÉPENSES", items: purchaseItems },
        { label: "FINANCES", items: financeItems },
        { label: "", items: footerItems, topSeparator: true },
      );
    }

    if (this.currentEmployee.employee_type === "OFFICE") {
      const officeItems = [];

      if (this.currentEmployee.employee_position_current.office_allow_sales_menu) {
        officeItems.push({ path: "/home", title: "Tableau de bord", icon: "heroHome", class: "" });
      }
      officeItems.push({ path: "/projects", title: "Chantiers", icon: "heroWrenchScrewdriver", class: "" });
      officeItems.push({ path: "/admin/team-planning", title: "Planning", icon: "heroCalendar", class: "" });

      if (this.currentEvaCompany.hasFeature(EvaFeatures.MAINTENANCE_CONTRACT)) {
        officeItems.push({ path: "/interventions", title: "Interventions", icon: "heroWrench", class: "" });
      }

      let salesItems = [];
      if (this.currentEmployee.employee_position_current.office_allow_sales_menu) {
        salesItems.push({ path: "/offers", title: "Offres", icon: "heroDocumentDuplicate", class: "" });
        if (this.currentEmployee.employee_position_current.office_allow_project_sensitive_data) {
          salesItems.push({
            path: "/invoices/issued",
            title: "Factures clients",
            icon: "heroDocumentArrowUp",
            class: "",
          });
        }
        salesItems.push({ path: "/customers", title: "Clients & Prospects", icon: "heroUsers", class: "" });
        salesItems.push({ path: "/catalog", title: "Catalogues", icon: "heroBookOpen", class: "" });
      }

      let purchaseItems = [];
      if (this.currentEmployee.employee_position_current.office_allow_expenses_menu) {
        purchaseItems.push({
          path: "/purchase-orders",
          title: "Bons de commande",
          icon: "heroDocumentText",
          class: "",
        });
        if (this.currentEmployee.employee_position_current.office_allow_project_sensitive_data) {
          purchaseItems.push({
            path: "/invoices/received",
            title: "Factures d'achat",
            icon: "heroDocumentArrowDown",
            class: "",
          });
        }
        purchaseItems.push({ path: "/suppliers", title: "Fournisseurs", icon: "heroUserGroup", class: "" });
      }

      let footerItems = [];

      if (this.currentEmployee.employee_position_current.office_allow_contact_menu) {
        officeItems.push({ path: "/contacts", title: "Contacts", icon: "heroBookOpen", class: "", category: "" });
      }

      if (officeItems.length > 0) {
        this.menuItems.push({ label: "", items: officeItems });
      }
      if (salesItems.length > 0) {
        this.menuItems.push({ label: "VENTES", items: salesItems });
      }
      if (purchaseItems.length > 0) {
        this.menuItems.push({ label: "DÉPENSES", items: purchaseItems });
      }
      if (footerItems.length > 0) {
        this.menuItems.push({ label: "", items: footerItems, topSeparator: true });
      }
    }

    if (this.currentEmployee.employee_type === "PROD") {
      this.menuItems.push({
        label: "",
        items: [{ path: "/home", title: "Tableau de bord", icon: "heroHome", class: "" }],
      });
    }

    if (this.currentEmployee.employee_type === "ACCOUNTANT") {
      const accountantItems = [
        { path: "/invoices/received", title: "Factures recues", icon: "heroDocumentArrowDown", class: "" },
        { path: "/invoices/issued", title: "Factures clients", icon: "heroDocumentArrowUp", class: "" },
        { path: "/expenses-report", title: "Notes de frais", icon: "heroCreditCard", class: "" },
      ];

      this.menuItems.push({ label: "", items: accountantItems });
    }
  }

  adminMenuItems: { path: string; title: string; icon: string }[] = [];

  private buildAdminMenuItems(): void {
    if (!this.currentEmployee?.is_superadmin) {
      this.adminMenuItems = [];
      return;
    }
    this.adminMenuItems = [
      { path: "/superadmin/eva-company", title: "Sociétés", icon: "heroLockClosed" },
      { path: "/superadmin/catalog", title: "Catalogues", icon: "heroBookOpen" },
      { path: "/superadmin/eva-tasks", title: "Eva tasks", icon: "heroDocumentText" },
    ];
  }

  navigateToCompany(): void {
    this.router.navigate(["/settings"], { queryParams: { section: "company" } });
  }

  toggleCollapse() {
    this.evaLayoutService.toggleMenuCollapsed();
  }

  openFormula(showPrices: boolean) {
    this.evaSubscriptionService.showFormulaPopup(null, showPrices);
  }

  hideCommercialItems() {
    localStorage.setItem(EvaSidebarComponent.HIDE_COMMERCIAL_ITEMS_KEY, "true");
    localStorage.setItem(EvaSidebarComponent.HIDE_COMMERCIAL_ITEMS_TIMESTAMP_KEY, Date.now().toString());
    this.showCommercialItems = false;
    this.updateMenuItems();
  }

  // --- Company selector methods ---

  fetchEvaCompanies(forceRefresh = false): void {
    if (this.companiesLoading) return;

    const cachedCompanies = !forceRefresh && JSON.parse(localStorage.getItem("currentEvaCompaniesAvailable"));
    if (cachedCompanies && cachedCompanies.length > 0) {
      this.currentEvaCompaniesAvailable = cachedCompanies;
      this.refreshEvaCompaniesDropdownOptions();
      this.dataLoaded = true;
      return;
    }

    this.companiesLoading = true;
    this.evaCompanyService
      .getAllLight()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.companiesLoading = false;
          this.dataLoaded = true;
        }),
      )
      .subscribe({
        next: (currentCompanies) => {
          this.currentEvaCompaniesAvailable = currentCompanies.map((c) => EvaCompany.createInstance(c));
          this.refreshEvaCompaniesDropdownOptions();
          this.evaCompanyService.updateEvaCompanies(this.currentEvaCompaniesAvailable);
          this.overlayFocusIndex = -1;
        },
        error: (err) => {
          this.evaService.showError("Impossible d’actualiser la liste des sociétés", "fetchEvaCompanies", err);
        },
      });
  }

  refreshEvaCompaniesDropdownOptions() {
    this.evaCompaniesDropdownOptions = this.currentEvaCompaniesAvailable.map((c) => ({
      label: c.name,
      value: c.eva_company_id,
    }));
  }

  selectCompany(companyId: string): void {
    if (companyId === this.currentEvaCompanyId) {
      this.companyDropdownVisible = false;
      return;
    }
    this.currentEvaCompanyId = companyId;
    this.companyDropdownVisible = false;
    this.changeEvaCompany();
  }

  changeEvaCompany(): void {
    const selected = this.currentEvaCompaniesAvailable.find((c) => c.eva_company_id === this.currentEvaCompanyId);
    if (selected) {
      this.evaCompanyService.changeCompany(selected.eva_company_id);
    }
  }

  toggleCompanyDropdown(): void {
    if (!this.hasMultipleCompanies) {
      this.router.navigate(["/home"]);
      return;
    }
    this.companyDropdownVisible = !this.companyDropdownVisible;
    if (!this.companyDropdownVisible) {
      this.companySearchQuery = "";
    }
  }

  toggleWorkspaceMenu(): void {
    this.workspaceMenuVisible = !this.workspaceMenuVisible;
    if (this.workspaceMenuVisible) {
      this.overlayFocusIndex = -1;
      setTimeout(() => this.companySearchInput?.nativeElement?.focus(), 0);
    } else {
      this.companySearchQuery = "";
      this.overlayFocusIndex = -1;
    }
  }

  closeWorkspaceMenu(): void {
    this.workspaceMenuVisible = false;
    this.companySearchQuery = "";
    this.overlayFocusIndex = -1;
    this.closeSidebar();
  }

  get isOfficeEmployee(): boolean {
    return this.currentEmployee?.employee_type === "OFFICE";
  }

  /** Ordered keys of menu items visible in the workspace overlay */
  get overlayMenuKeys(): string[] {
    const keys: string[] = [];
    if (!this.isOfficeEmployee) keys.push("company");
    keys.push("profile");
    if (this.hasAccessToPlanning) keys.push("working-activity");
    keys.push("logout");
    return keys;
  }

  /** Total navigable items in the overlay */
  private get overlayItemCount(): number {
    const companyCount = this.hasMultipleCompanies ? this.filteredCompanies.length : 0;
    return companyCount + this.overlayMenuKeys.length;
  }

  /** Base index for a menu item key (after companies) */
  getMenuItemBaseIndex(menuKey: string): number {
    const companyCount = this.hasMultipleCompanies ? this.filteredCompanies.length : 0;
    const idx = this.overlayMenuKeys.indexOf(menuKey);
    return idx === -1 ? -1 : companyCount + idx;
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest(".eva-sidebar__company-refresh") && (event.key === "Enter" || event.key === " ")) return;
    if (
      event.key === "Tab" &&
      !event.shiftKey &&
      target === this.companySearchInput?.nativeElement &&
      this.currentEvaCompaniesAvailable.length > 10 &&
      !this.companiesLoading
    )
      return;

    const total = this.overlayItemCount;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.overlayFocusIndex = this.overlayFocusIndex < total - 1 ? this.overlayFocusIndex + 1 : 0;
        this.scrollOverlayItemIntoView();
        break;

      case "ArrowUp":
        event.preventDefault();
        this.overlayFocusIndex = this.overlayFocusIndex > 0 ? this.overlayFocusIndex - 1 : total - 1;
        this.scrollOverlayItemIntoView();
        break;

      case "Tab":
        event.preventDefault();
        if (event.shiftKey) {
          this.overlayFocusIndex = this.overlayFocusIndex > 0 ? this.overlayFocusIndex - 1 : total - 1;
        } else {
          this.overlayFocusIndex = this.overlayFocusIndex < total - 1 ? this.overlayFocusIndex + 1 : 0;
        }
        this.scrollOverlayItemIntoView();
        break;

      case "Enter":
        event.preventDefault();
        this.executeOverlayAction(this.overlayFocusIndex);
        break;

      case "Escape":
        event.preventDefault();
        this.workspaceMenuVisible = false;
        this.companySearchQuery = "";
        this.overlayFocusIndex = -1;
        break;
    }
  }

  private executeOverlayAction(index: number): void {
    if (index < 0) return;

    const companyCount = this.hasMultipleCompanies ? this.filteredCompanies.length : 0;

    if (index < companyCount) {
      this.selectCompany(this.filteredCompanies[index].eva_company_id);
      return;
    }

    const menuIndex = index - companyCount;
    const key = this.overlayMenuKeys[menuIndex];
    switch (key) {
      case "company":
        this.navigateToCompanySettings();
        break;
      case "profile":
        this.navigateToProfile();
        break;
      case "working-activity":
        this.navigateToWorkingActivity();
        break;
      case "logout":
        this.logout();
        break;
    }
  }

  private scrollOverlayItemIntoView(): void {
    setTimeout(() => {
      const focused = document.querySelector(".eva-sidebar__workspace-overlay .focused");
      focused?.scrollIntoView({ block: "nearest" });
    }, 0);
  }

  navigateToCompanySettings(): void {
    this.router.navigate(["/settings"], { queryParams: { section: "company" } });
    this.closeWorkspaceMenu();
  }

  /** Handles overlay link clicks — Ctrl+Click / middle-click open in new tab via href, normal click uses router */
  onOverlayLink(event: MouseEvent, section: "company" | "profile" | "working-activity"): void {
    if (event.ctrlKey || event.metaKey || event.button === 1) {
      // Let the browser handle the href (new tab)
      this.closeWorkspaceMenu();
      return;
    }
    event.preventDefault();
    if (section === "company") {
      this.navigateToCompanySettings();
    } else if (section === "working-activity") {
      this.navigateToWorkingActivity();
    } else {
      this.navigateToProfile();
    }
  }

  navigateToAdmin(path: string): void {
    this.router.navigate([path]);
    this.closeWorkspaceMenu();
  }

  getCompanyInitials(companyId?: string): string {
    const id = companyId || this.currentEvaCompanyId;
    const company = this.currentEvaCompaniesAvailable.find((c) => c.eva_company_id === id);
    if (!company?.name) return "";
    if (company.name_initials?.trim()) return company.name_initials.toUpperCase();
    return company.name
      .split(" ")
      .filter((part) => part.length > 0 && !["SA", "SAS", "LLC"].includes(part))
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }

  getCurrentCompanyName(): string {
    const company = this.currentEvaCompaniesAvailable.find((c) => c.eva_company_id === this.currentEvaCompanyId);
    return company?.name || "";
  }

  getCurrentCompanyColor(): string {
    if (!this.dataLoaded) return "#000000";
    const company = this.currentEvaCompaniesAvailable.find((c) => c.eva_company_id === this.currentEvaCompanyId);
    return company?.company_color || "#000000";
  }

  getCompanyColor(companyId: string): string {
    const company = this.currentEvaCompaniesAvailable.find((c) => c.eva_company_id === companyId);
    return company?.company_color || "#000000";
  }

  // --- User profile methods ---

  async loadAvatar() {
    this.userImagePath = await this.employeesService.getMeAvatar();
  }

  toggleUserMenu() {
    this.userMenuVisible = !this.userMenuVisible;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest(".eva-sidebar__user") && !target.closest(".eva-sidebar__user-mini")) {
      this.userMenuVisible = false;
    }
    if (!target.closest(".eva-sidebar__workspace") && !target.closest(".eva-sidebar__workspace-mini")) {
      this.companyDropdownVisible = false;
      this.workspaceMenuVisible = false;
      this.companySearchQuery = "";
    }
  }

  navigateToProfile() {
    this.router.navigate(["/settings"], { queryParams: { section: "profile" } });
    this.userMenuVisible = false;
    this.workspaceMenuVisible = false;
    this.closeSidebar();
  }

  navigateToWorkingActivity() {
    this.router.navigate(["/employee/working-activity"]);
    this.userMenuVisible = false;
    this.closeWorkspaceMenu();
  }

  logout() {
    this.evaSubscriptionService.logout();
    this.router
      .navigate(["/login"])
      .then(() => {
        this.authService.logout();
      })
      .catch((error) => {
        console.error("Navigation error:", error);
      });
  }

  getEmployeeRoleLabel(): string {
    switch (this.currentEmployee?.employee_type) {
      case "ADMIN":
        return "Administrateur";
      case "OFFICE":
        return "Employe de bureau";
      case "PROD":
        return "Chef de chantier";
      case "ACCOUNTANT":
        return "Comptable";
      default:
        return "";
    }
  }
}
